import React, {ChangeEvent, ReactElement} from 'react';
import {connect} from "react-redux";
import './TransactionCard.less';
import '../../stylesheets/form.less';
import Transaction from "../../entities/transaction";
import util from "../../lib/util";
import * as DateUtil from "../../lib/dateutils";
import OkCancel from "../../ui/Buttons/OkCancel";
import FormElement from "../../lib/FormElement";
import Matter from "../../entities/matter";
import CaseHelper from "../../Case/CaseHelper";
import IconButton from "../../ui/IconButton/IconButton";
import Contact from "../../entities/contact";
import FinancialHelper from "../FinancialHelper";
import failureLadder from '../../lib/failureLadder';
import i18n from '../../i18n/i18n';
import HelpMessage from '../../i18n/HelpMessage';
import {formatFMLFromId, formatLFM, sortContacts} from "../../Contacts/ContactFormat";
import find from "../../store/find";
import Icons from "../../ui/IconButton/Icons";
import type {RootState} from "../../@types/store";
import type {SelectOption} from "../../@types/select";
import {TransactionChildCardProps} from "./TransactionCard";
import {filterTrustOptions} from "./trustPicker";

type AccountInfo = {
    id: string
    type: string
    name: string | ReactElement,
    balance: number
};

/**
 * See documentation for the Transaction schema (schemas/transaction.js) for a full rundown on how the Transaction works, and which
 * bits are used for each type of transaction.
 *
 * This card can be called from either an EditableTable or directly from a button press.  The env and startEditing props are defaulted
 * so that coming in from outside of an EditableTable still works.
 *
 * There are two ways that this card is shown: from an EditableTable of transactions in Financials, and directly from a Matter or Contact.
 * Check props.env.matter and props.env.contact to see if either of these cases are true.
 *
 * If we are called from a Matter, we will assume that the matter is unchangeable, but the contact (if > 1) is.
 * If we are called from a Contact, we will assume that the contact is unchangeable, but the Matter is.
 */
const BalanceAdjustmentCard: React.FC<BalanceAdjustmentCardProps> = (props) => {

    // OWA-552: guarantee a non-null `current` transaction in code, not via a prop-type
    // contract — this card never depends on the parent always passing one. currentTx is the
    // parent's live current in practice; the fallback only materialises on the warned path.
    if (!props.current && import.meta.env.DEV) console.warn('<BalanceAdjustmentCard/> rendered without a current Transaction; using a blank fallback.');
    const currentTx = props.current ?? new Transaction({});

    // Helper for brevity.
    const l = (key: string, params?: object): string => i18n.ls('transaction', key, params);

    /**
     * Get a list of matters.
     *
     * For TT, only show clients that have funds in trust (matter or general).
     */
    const getClients = (tr: Transaction): SelectOption<string, Contact>[] => {
        // Get a unique list of all clients
        const matterClients = CaseHelper.getClients(true) as string[];

        const leadClients = util.flatten(props.leads.map(l => l.clients));

        const getIcon = (c: Contact) => {
            if (matterClients.includes(c.id)) return Icons.CASE;
            else return Icons.LEAD;
        };

        const getColor = (c: Contact) => {
            if (matterClients.includes(c.id)) return '#b3953c';
            else return 'green';
        };

        const clients = matterClients.concat(leadClients)
                                     .filter(util.distinctFilter)
                                     .filter(util.existsFilter)
                                     .map(cid => props.contacts.get(cid))
                                     .sort(sortContacts)
                                     .map(c => {
                                         const name = formatLFM(c);
                                         return {
                                             label: <IconButton type={getIcon(c)} color={getColor(c)} label={name}/>,
                                             value: name,
                                             data: c
                                         };
                                     });

        // Filter based on state, rather than the transaction
        if (tr) {
            // Only return entries for the clients in state
            return clients.filter(c => tr.clientIds.includes(c.data.id));
        }
        else return clients;
    };

    /**
     * Get a list of matters.  Only show matters where the selected clients are available.
     */
    const getMatters = (tr: Transaction): SelectOption<string, Matter>[] => {
        // Find all matters related to these clients.
        const matters = props.matters
                            .filter(m => currentTx.clientIds.every(cid => m.clients.includes(cid)))
                            .map(m => {
                                return {label: m.name, value: m.name, data: m};
                            });

        if (tr) {
            return matters.filter(m => tr.matterIds.includes(m.data.id));
        }
        else return matters;
    };

    const onClientsChange = (values: SelectOption<string, Contact>[]) => {
        const current = currentTx.clone();

        // We always set the clientIds field
        current.clientIds = values.map(v => v.data.id);
        if (current.txType === 'ST') {
            if (!current.target) {
                current.targetType = 'G';
                current.target = current.clientIds[0];
            }
        }

        props.onChange(current);
    };

    const onTransactionDateChanged = (dt: Date) => {
        const current = currentTx.clone();
        current.txDate = dt;

        props.onChange(current);
    };

    const onTrustAccountsChange = (e: SelectOption<string, AccountInfo>) => {
        const targetType = e.data.type;
        const target = e.data.id;

        const current = currentTx.clone();

        current.targetType = targetType;
        current.target = target;

        props.onChange(current);
    };

    /**
     * Get trust accounts for the clients and matters.
     */
    const getTrustAccounts = (tr: Transaction): SelectOption<string, AccountInfo>[] => {
        // Otherwise get matters specific to the case
        const matters = props.matter
                        ? [props.matter]
                        : currentTx.matterIds.length > 0
                          ? currentTx.matterIds.map(mid => CaseHelper.getMatterFromId(mid))
                          : props.matters.filter(m => util.arrayIncludesAllTerms(currentTx.clientIds, m.clients));

        const readOnly = props.readOnly || currentTx.invoiceId;

        // If not clients in common, we can not pull from any client trusts.
        const clientTrusts = currentTx.clientIds
                                 .map(cid => {
                                     // Get the balance for this trust
                                     const contact = find<Contact>('contacts', cid);
                                     const clientName = formatLFM(contact, true);
                                     const trustBalance = FinancialHelper.getTrustAmount('G', cid);

                                     return {
                                         id: cid,
                                         type: 'G',
                                         name: FinancialHelper.formatLabel(Icons.USER, i18n.cvs('trustFor', {value: clientName}), trustBalance, 'blue'),
                                         balance: trustBalance
                                     };
                                 });

        // Add the trust for the matter
        const matterTrusts = matters
            .map(matter => {
                const trustAmount = FinancialHelper.getTrustAmount('M', matter.id);

                return {
                    id: matter.id,
                    type: 'M',
                    name: FinancialHelper.formatLabel(Icons.CASE, i18n.cvs('trustFor', {value: matter.name}), trustAmount, 'brown'),
                    balance: trustAmount
                };
            });

        const ordered = props.matter ? [...matterTrusts, ...clientTrusts] : [...clientTrusts, ...matterTrusts];

        // The trust has to have at least as many funds as the amount (if not read-only); the selected trust stays visible.
        const merged = filterTrustOptions(ordered, {
            minBalance: readOnly ? undefined : currentTx.amount,
            keepIds: [currentTx.source, currentTx.target]
        });

        const result = merged.map(account => {
            return {
                label: account.name,
                value: account.id,
                data: account
            };
        });

        // Filter based on state, rather than the transaction
        if (tr) {
            // Only return entries matching the passed in transaction
            return result.filter(t => (tr.targetType === t.data.type) && (tr.target === t.data.id));
        }
        else return result;
    };

    const renderClientName = (element: FormElement) => {
        const tr = currentTx;

        let label = tr.txType === 'BA' ? i18n.cs('client', true)
                                       : tr.txType === 'ST' ? l('bClients')
                                                            : l('bClient');
        let value: string;

        if (props.client) {
            value = formatLFM(props.client, true);
        }
        else if (props.matter) {
            label = i18n.cs('clients', true);

            value = props.matter.clients
                        .map(cid => formatFMLFromId(cid, false))
                        .join(', ');
        }
        else {
            const clients = currentTx.clientIds.map(cid => formatFMLFromId(cid));
            if (clients.length > 1) label = i18n.cs('clients', true);
            value = clients.join(', ');
        }

        return element.renderElement('clientName', label, <div>{value}</div>, true);
    };

    const renderMatterName = (element: FormElement) => {
        return element.renderElement('matterName', 'Case', <div>{props.matter?.name || '???'}</div>, true);
    };

    /**
     * Render the matter name, if a matter is specified.
     */
    const renderMatterNames = (element: FormElement) => {
        return element.renderElement('matterNames',
            l('matterIds'),
            <div key='matters'>
                {currentTx.matterIds.map(mid => CaseHelper.getMatterFromId(mid)?.name)}
            </div>, true);
    };

    const onAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
        const current = currentTx.clone();
        current.amount = e.target.value === '-' ? 0 : Number.parseFloat(e.target.value);

        props.onChange(current);
    };

    const element = props.element;
    const tr = currentTx;
    const readOnly = props.readOnly || tr.invoiceId;

    // We can now determine if we are clear to save.
    const disabled =
        !props.hasChanged ||
        !tr.txDate ||
        tr.clientIds.length === 0 ||
        tr.matterIds.length === 0 ||
        tr.amount === 0 ||
        (!['SA', 'ST'].includes(tr.txType) && !tr.target) ||
        !tr.targetType ||
        !tr.target ||
        !tr.isValid();

    const disabledMessage = failureLadder([
        [!tr.txDate, l('need', {v: 'date'})],
        [tr.clientIds.length === 0, l('need', {v: 'client'})],
        [tr.matterIds.length === 0, l('need', {v: 'case'})],
        [tr.amount === 0, l('need', {v: 'amount', an: true})],
        [!['SA', 'ST'].includes(tr.txType) && !tr.target, l('need', {v: 'target account'})],
        [!tr.targetType, 'No target selected'],
        [!tr.target, 'No target selected'],
        [!tr.isValid(), tr.validationError]
    ]);

    const isNull = tr.amount === null || tr.amount === undefined;
    const creditDebit = tr.txType === 'BA'
                        ? tr.amount > 0 ? l('credit') : l('debit')
                        : l('incomingBalance');
    const cdStyle = creditDebit.replace(/\s/g, '-');

    if (readOnly) return (
        <>
            {/* Button bar */}
            {(props.onOk && props.onCancel) &&
             <OkCancel label={i18n.ls('transaction', 'title')}
                       disabled={true}
                       onSubmit={props.onOk}
                       onCancel={props.onCancel}
                       onTop={true}
             />
            }

            {/* Transaction Date */}
            {element.renderElement('txDate',
                <HelpMessage field='date' module='transaction'/>,
                DateUtil.formatDate(tr.txDate))}

            {/* Client(s) */}
            {renderClientName(element)}

            {/* Matter(s) */}
            {props.matter && renderMatterName(element)}
            {!props.matter && (tr.clientIds.length > 0) && (tr.matterIds.length > 0) && renderMatterNames(element)}

            {/* Amount */}
            {element.renderElement('amount-ba',
                <HelpMessage module='transaction' field='amount' params={{txType: tr.txType}}/>,
                <div key='amount' className={`transaction-card__${cdStyle}`}>
                    {util.formatCurrency(tr.amount, true)} {creditDebit.toUpperCase()}
                </div>
            )}

            {/* Notes */}
            {element.renderMultilineTextOnly('notes', <HelpMessage module='transaction' field='notes'/>, false)}
        </>
    );
    else return (
        <>
            {/* Button bar */}
            {(props.onOk && props.onCancel) &&
             <OkCancel label={i18n.ls('transaction', 'title')}
                       disabled={!!disabled}
                       disabledMessage={disabledMessage}
                       useDisabledMessage={true}
                       onCancel={props.onCancel}
                       onSubmit={props.onOk}
                       onTop={true}
             />
            }

            {/* Transaction Date */}
            {element.renderDateTime('txDate',
                <HelpMessage field='date' module='transaction'/>,
                onTransactionDateChanged, false, true)}

            {/* Client(s) */}
            {(props.client || props.matter)
             ? renderClientName(element)
             : element.renderSelect('clients',
                    tr.txType === 'ST'
                    ? <HelpMessage module='transaction' field='bClient'/>
                    : <HelpMessage module='transaction' field='bClients'/>,
                    true, false, getClients, onClientsChange, true)
            }

            {/* Matter(s) */}
            {props.matter && renderMatterName(element)}

            {/* Trust, if ST */}
            {tr.txType === 'ST' && element.renderSelect(
                'trust',
                <HelpMessage field='trust' module='transaction'/>,
                false,
                false,
                getTrustAccounts,
                onTrustAccountsChange,
                true
            )}

            {/* Amount */}
            {element.renderElement('amount-ba',
                <HelpMessage module='transaction' field='amount' params={{txType: tr.txType}}/>,
                <>
                    <div className="dollar-sign">$</div>
                    <input className='money'
                           key='amount'
                           defaultValue={isNull ? 0 : tr.amount}
                           autoFocus={false}
                           type="number" step="0.01" min="-9999999999.99" max="9999999999.99"
                           required={true}
                           onBlur={onAmountChange}
                    />
                    <div className={`transaction-card__${cdStyle}`}>{creditDebit.toUpperCase()}</div>
                </>,
                true)}

            {/* Notes */}
            {element.renderMultilineTextOnly('notes', <HelpMessage module='transaction' field='notes'/>, false)}
        </>
    );
};

export type BalanceAdjustmentCardProps = TransactionChildCardProps & Pick<RootState, 'bankAccounts' | 'contacts' | 'leads' | 'matters' | 'transactions'>;

const mapStateToProps = (state: RootState) => {
    return {
        bankAccounts: state.bankAccounts,
        contacts: state.contacts,
        leads: state.leads,
        matters: state.matters,
        transactions: state.transactions,
    };
};


export default connect(mapStateToProps)(BalanceAdjustmentCard);
